/* eslint-disable react/destructuring-assignment */
import React, { Component, Fragment } from 'react';

import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';

import { Container, Header, Loader } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/Dashboard.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { ASSIGNMENT_MENU_STATUS, ASSIGNMENT_STATUS } from '../managers/AssignmentManager';
import { CONTENT_ITEM_TYPES, CONTENT_MODE } from '../managers/ContentManager';
import { DIALOG_NAMES } from '../managers/DialogManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';

import AssignmentService from '../services/AssignmentService';
import ImageService from '../services/ImageService';
import ResourcePacingService from '../services/ResourcePacingService';
import StudentAssignmentPlayerLauncherService from '../services/StudentAssignmentPlayerLauncherService';
import StudentHelperService from '../services/StudentHelperService';

import { dateFormat, getSessionStorageItem, removeSessionStorageItem } from '../utils';

export default @inject(
  'accessManager',
  'appManager',
  'assignmentManager',
  'classroomManager',
  'contentManager',
  'courseManager',
  'dialogManager',
  'dynamicSatelliteManager',
  'navigationManager',
  'studentAssignmentManager',
  'studentContentCardManager',
  'studentCourseDrivenExperienceManager',
  'studentProgressManager',
  'tagContentManager',
  'userManager'
)
@observer
class StudentDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentCourseButtonElement: null,
      hasMore: true,
      loadingStudentDashboard: true
    };

    this.BookImageCard = SatCoreComponent('BookImageCard');
    this.CarouselContainer = SatCoreComponent('CarouselContainer');
    this.ClassCard = SatCoreComponent('ClassCard');
    this.DeprecatedStudentAssignmentCard = SatCoreComponent('DeprecatedStudentAssignmentCard');
    this.NoAssignmentsMessage = SatCoreComponent('NoAssignmentsMessage');
    this.StudentAssignmentPlayerLauncher = SatCoreComponent('StudentAssignmentPlayerLauncher');
    this.StudentCourseDrivenAssignments = SatCoreComponent('StudentCourseDrivenAssignments');
    this.StudentCourseDrivenCarousel = SatCoreComponent('StudentCourseDrivenCarousel');
    this.StudentCourseDrivenExperienceView = SatCoreComponent('StudentCourseDrivenExperienceView');
    this.StudentDashboardProgressWidget = SatCoreComponent('StudentDashboardProgressWidget');
  }

  async componentDidMount() {
    const {
      appManager,
      assignmentManager,
      dynamicSatelliteManager,
      navigationManager,
      studentAssignmentManager,
      studentCourseDrivenExperienceManager
    } = this.props;

    const { allowStudentCourseDrivenExperience } = studentCourseDrivenExperienceManager;

    const { isDynamicSatellite } = dynamicSatelliteManager;

    const assignmentLinkId = getSessionStorageItem('c2c_assignmentLinkId');

    // ensure we are still on the StudentDashboard before loading its data
    if (!isDynamicSatellite || assignmentLinkId || window.location.pathname === '/') {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has('view')) {
        navigationManager.setView(urlParams.get('view'));
      } else {
        navigationManager.setView(VIEW_SELECTION.DASHBOARD);
      }
      const status = !allowStudentCourseDrivenExperience ? (
        [ASSIGNMENT_STATUS.LOCKED, ASSIGNMENT_STATUS.READY, ASSIGNMENT_STATUS.STARTED]
      ) : [];
      if (status.length) {
        studentAssignmentManager.setCurrentStudentAssignmentMenuStatus(ASSIGNMENT_MENU_STATUS.CURRENT);
        assignmentManager.setAssignmentStatus(status);
      }

      // if we have an assignmentLinkId, we need to attempt to launch a student assignment,
      // as this was set from an external launch link.
      if (assignmentLinkId) {
        // TODO remove // removeSessionStorageItem('c2c_assignmentLinkId');
        await this.launchStudentLinkAssignment(assignmentLinkId);

        this.setState({ loadingStudentDashboard: false });
      }

      if (!appManager.initializingApp && !allowStudentCourseDrivenExperience) {
        await StudentHelperService.fetchStudentAssignments({ status });
        this.setState({ loadingStudentDashboard: false });
      } else {
        this.setState({ loadingStudentDashboard: false });
      }
    }
  }

  launchStudentLinkAssignment = async (assignmentLinkId) => {
    const { assignmentManager, navigationManager, userManager } = this.props;

    navigationManager.setLoadingAssignmentLinkLaunched(true);

    this.setState({
      loadingStudentDashboard: true
    });
    let errorMessage = null;
    if (!userManager.isStudent) {
      this.showTextMessage('error', 'You do not have access to this assignment');
      navigationManager.setLoadingAssignmentLinkLaunched(true);
      setTimeout(() => {
        navigationManager.setAssignmentLinkLaunched(true);
        removeSessionStorageItem('c2c_assignmentLinkId');
      }, 500);
      return;
    }
    const response = await assignmentManager.fetchStudentActivity(assignmentLinkId);
    if (response) {
      if (response.status !== 'SUCCESS') {
        errorMessage = response.statusMessage;
      } else {
        const assignment = response.data;
        const activityInstance = response?.activityInstance;
        if (assignment && activityInstance) {
          let contentMode = null;
          const isOpen = ASSIGNMENT_STATUS.getOpenStatuses().includes(assignment.status);
          // eslint-disable-next-line max-len
          const isReview = assignment.studentReview && (assignment.status === ASSIGNMENT_STATUS.COMPLETED || (assignment.status === ASSIGNMENT_STATUS.CLOSED && assignment.submitted));
          if (isOpen || isReview) {
            contentMode = isOpen ? CONTENT_MODE.ASSESS : CONTENT_MODE.REVIEW;
            const isFileResource = assignment.contentItemEntityTypeId === CONTENT_ITEM_TYPES.FILE_RESOURCE;
            if (isFileResource) { // Handle FILE_RESOURCE content type as a special case
              // i.e. current flow requires downloading the FILE_RESOURCE directly
              const fileDownloadUrl = CONTENT_ITEM_TYPES.getFileDownloadUrl(assignment.contentItemId);
              window.open(fileDownloadUrl);
              // Signal that assignment link was successfully launched
              navigationManager.setAssignmentLinkLaunched(true);
              removeSessionStorageItem('c2c_assignmentLinkId');
            } else { // Default flow for all other content types
              // setup params for presentation
              const params = {
                assignment: { ...assignment, ...activityInstance },
                contentMode,
                turnInLate: false
              };
              // open the linked assignment
              await StudentAssignmentPlayerLauncherService.launchAssignment(params);
            }
          } else {
            errorMessage = 'You do not have access to this assignment';
          }
        } else {
          errorMessage = 'Assignment not found.';
        }
      }
    } else {
      errorMessage = 'Assignment not found.';
    }
    if (errorMessage) {
      this.showTextMessage('Error', errorMessage);
      setTimeout(() => {
        navigationManager.setAssignmentLinkLaunched(true);
        removeSessionStorageItem('c2c_assignmentLinkId');
      }, 500);
    }
  }

  showTextMessage = (title, message) => {
    const { dialogManager } = this.props;
    if (message) {
      dialogManager.setOpenDialog(DIALOG_NAMES.TEXT, {
        title,
        message
      }, () => dialogManager.closeDialog(DIALOG_NAMES.TEXT));
    }
  }

  handleSelfPacedOpen = async (contentMode, assignment) => {
    const { assignmentManager, classroomManager } = this.props;
    // first, create the assignment
    const success = await AssignmentService.createStudentPacedAssignment(
      assignment.contentItemId, assignment.courseResourceElementId, classroomManager.currentClassroomId, assignment.courseContentItemId);
    // then call to open it.
    if (success && assignmentManager.getLastAddedAssignment()) {
      StudentAssignmentPlayerLauncherService.launchAssignment({
        assignment: assignmentManager.getLastAddedAssignment(),
        contentMode
      });
    }
  }

  renderAssignmentCards = (assignments) => {
    const {
      accessManager,
      assignmentManager,
      studentCourseDrivenExperienceManager,
      t
    } = this.props;

    const { policies } = accessManager;
    const { studentDashboard } = policies;
    const { assignmentCard } = studentDashboard;

    const {
      allowStudentCourseDrivenAssignmentFiltering,
      allowStudentCourseDrivenExperience
    } = studentCourseDrivenExperienceManager;

    const { DeprecatedStudentAssignmentCard } = this;
    const { NoAssignmentsMessage } = this;

    const hideCourseTitle = allowStudentCourseDrivenExperience && allowStudentCourseDrivenAssignmentFiltering;

    const menuStatus = !allowStudentCourseDrivenExperience ? ASSIGNMENT_MENU_STATUS.CURRENT : (
      studentCourseDrivenExperienceManager.selectedCourseDrivenAssignmentMenuStatus
    );
    const assignmentsJsx = (assignments || assignmentManager.assignmentArray || []).map((assignment) => {
      const visibilityMode = ResourcePacingService.visibilityMode(assignment);
      const contentImageUrl = ImageService.getImageUrl(assignment);
      return visibilityMode !== 'hidden_from_students' && (
        <div key={`AAC_${assignment.id}`} className='dash-card ui items student-dashboard-assignment-card-wrapper'>
          <DeprecatedStudentAssignmentCard
            additionalClassNames={classNames('student-dashboard-assignment-card', {
              'student-default-dashboard-assignment-card': !allowStudentCourseDrivenExperience
            })}
            assignment={assignment}
            contentImageUrl={contentImageUrl}
            disabled={!assignmentCard || assignment.licenseExpired}
            handleDownload={this.handleDownload}
            handlePresent={StudentAssignmentPlayerLauncherService.launchAssignment}
            handleSelfPacedOpen={this.handleSelfPacedOpen}
            hideCourseTitle={hideCourseTitle}
            menuStatus={menuStatus} />
        </div>
      );
    }).filter((assignmentJsx) => assignmentJsx);
    return assignmentsJsx?.length ? assignmentsJsx : (
      <NoAssignmentsMessage message={t('NoAssignmentsYet', 'No assignments yet')} />
    );
  };

  showBook = async (courseId) => {
    const { classroomManager, courseManager, history, navigationManager } = this.props;
    courseManager.setCurrentElementId('');
    courseManager.setCurrentCourseId(courseId);
    navigationManager.setView(VIEW_SELECTION.BOOK);
    await navigationManager.navigateToClassroomSkipSingleCourse(classroomManager.currentClassroomId,
      1, // always 1 for student so we skip the course selection page
      courseManager.currentCourseId,
      VIEW_SELECTION.BOOK,
      history,
      false);
  };

  showProgress = async () => {
    this.props.navigationManager.setView(VIEW_SELECTION.PROGRESS);
    await this.props.history.push(`/progress?view=${VIEW_SELECTION.PROGRESS}`);
  };

  getBookCardContent = () => {
    const {
      accessManager,
      // accessManager: { policies: { studentDashboard: { bookImageCard } } },
      assignmentManager, classroomManager, courseManager, history, navigationManager
    } = this.props;

    const shouldDisableBookImageCard = accessManager?.policies?.studentDashboard?.bookImageCard === false;

    const { currentCourseButtonElement } = this.state;

    const { BookImageCard, CarouselContainer } = this;

    const { customDefaultAssignmentCardImg } = assignmentManager;

    const { currentClassroomId } = classroomManager;

    if (this.props.getBookCardContent !== undefined) { // eslint-disable-line react/destructuring-assignment
      return this.props.getBookCardContent(this.props, this); // eslint-disable-line react/destructuring-assignment
    }

    const getButtonAndSetTabIndex = () => {
      const activeButtonElement = document.querySelector('.slick-active .open-course-button');
      if (activeButtonElement) {
        activeButtonElement.tabIndex = 0;
        this.setState({ currentCourseButtonElement: activeButtonElement });
      }
    };

    const bookSettings = {
      afterChange: getButtonAndSetTabIndex,
      beforeChange: () => {
        if (currentCourseButtonElement) {
          currentCourseButtonElement.tabIndex = -1;
        }
      },
      dots: false,
      infinite: true,
      onInit: getButtonAndSetTabIndex,
      slidesToScroll: 1,
      slidesToShow: 1,
      speed: 500
    };

    const courseList = courseManager.getCourseList(currentClassroomId);

    return (
      <div className={classNames('w-content w-content-book-card-container', {
        'allow-carousel-adaptive-height': navigationManager.allowCarouselAdaptiveHeight
      })}>
        <CarouselContainer {...bookSettings}>
          {courseList.map((course, index) => (
            <BookImageCard
              key={`csc_${course.id}`}
              course={course}
              customDefaultCardImg={customDefaultAssignmentCardImg}
              disabled={shouldDisableBookImageCard}
              history={history}
              index={index}
              showBook={this.showBook} />
          )
          )}
        </CarouselContainer>
      </div>
    );
  }

  renderDefaultStudentCourseCarouselWidget = () => {
    const { t } = this.props;
    return (
      <div aria-label={`${t('BookLabel', 'My Content')} Carousel`}
        className='dashboard-widget student-dashboard-widget-carousel red' tabIndex={0}>
        <h2 className='w-title' tabIndex='0'>{t('BookLabel', 'Missing book label translation')}</h2>
        {this.getBookCardContent()}
      </div>
    );
  }

  renderStudentDashboardProgressWidgets = () => {
    const { accessManager: { policies: { studentDashboard: { progressWidget } } }, classroomManager, studentProgressManager } = this.props;
    const { t } = this.props;
    const { trophyCounts, meterCounts } = studentProgressManager;
    studentProgressManager.setClassroom(classroomManager.currentClassroomId);

    const ProgressWidgetOverride = SatCoreComponent('ProgressWidget');
    if (ProgressWidgetOverride !== Fragment) {
      return (
        <ProgressWidgetOverride
          meterCounts={meterCounts}
          progressWidget={progressWidget}
          showProgress={this.showProgress}
          t={t}
          trophyCounts={trophyCounts} />
      );
    } else if (studentProgressManager.allowedStudentDashboardProgressWidgets?.length) {
      const { StudentDashboardProgressWidget } = this;
      const allowedStudentDashboardProgressWidgetKeys = toJS(studentProgressManager.allowedStudentDashboardProgressWidgets);
      return allowedStudentDashboardProgressWidgetKeys.map((widgetKey) => {
        return (
          <React.Fragment key={widgetKey}>
            <StudentDashboardProgressWidget
              disabled={!progressWidget}
              showProgress={this.showProgress}
              widgetKey={widgetKey} />
          </React.Fragment>
        );
      });
    } else {
      return null;
    }
  }

  renderAssignmentsWidget = () => {
    const { t } = this.props;
    return (
      <div className='dashboard-widget student-dashboard-widget-assignments green'>
        <h2 className='w-title' tabIndex='0'>{t('AssignmentLabel', 'Missing assignment label translation')}</h2>
        <div className='w-content'>
          <div className='ui items scroller'>
            {this.renderAssignmentCards()}
          </div>
        </div>
      </div>
    );
  }

  renderStudentCourseDrivenExperience = () => {
    const { loadingStudentDashboard } = this.state;
    const { StudentCourseDrivenExperienceView } = this;
    return (
      <StudentCourseDrivenExperienceView
        handlePresent={StudentAssignmentPlayerLauncherService.launchAssignment}
        loadingStudentDashboard={loadingStudentDashboard}
        renderAssignmentCards={this.renderAssignmentCards}
        renderDefaultStudentCourseCarouselWidget={this.renderDefaultStudentCourseCarouselWidget}
        renderDefaultStudentDashboardHeaderContainer={this.renderDefaultStudentDashboardHeaderContainer} />
    );
  }

  renderDefaultStudentDashboard() {
    const { appManager, classroomManager, navigationManager, userManager } = this.props;

    const { loadingAssignmentLinkLaunched } = navigationManager;

    const { loadingStudentDashboard } = this.state;

    const loading = loadingAssignmentLinkLaunched || loadingStudentDashboard || appManager.initializingApp;

    return loading ? <Loader active /> : (
      <Container className={classNames('dashboard-backdrop', 'student-dashboard-backdrop')} fluid>
        <Container className={classNames('dashboard-container', 'student-dashboard-container')}>
          {this.renderDefaultStudentDashboardHeaderContainer()}
          <Container className='widget-wrapper'>
            {(userManager.loaded && classroomManager.loaded/* && course !== null */) ? (
              <>
                <Container className='left-col'>
                  {this.renderDefaultStudentCourseCarouselWidget()}
                  {this.renderStudentDashboardProgressWidgets()}
                </Container>
                <Container className='right-col'>
                  {this.renderAssignmentsWidget()}
                </Container>
              </>
            ) : null}
          </Container>
        </Container>
      </Container>
    );
  }

  renderDefaultStudentDashboardHeaderContainer = () => {
    const date = new Date();

    const { appManager, t, userManager } = this.props;

    return (
      <div className='headerContainer'>
        <div className='headerWrapper'>
          <Header as='h1' className='welcome-header theme-header-title'>
            {t('WelcomeLabel', 'Welcome')}&nbsp;
            {userManager.firstName}
            !
          </Header>
          <div
            className='welcome-date'>
            {dateFormat(date, 'fullDate', appManager.locale)}
          </div>
        </div>
      </div>
    );
  }

  renderMainContent = () => {
    const { studentCourseDrivenExperienceManager } = this.props;
    if (studentCourseDrivenExperienceManager.allowStudentCourseDrivenExperience) {
      return this.renderStudentCourseDrivenExperience();
    }
    return this.renderDefaultStudentDashboard();
  }

  render() {
    const { StudentAssignmentPlayerLauncher } = this;
    return (
      <>
        <StudentAssignmentPlayerLauncher />
        {this.renderMainContent()}
      </>
    );
  }
}

SatCoreRegister('StudentDashboard', StudentDashboard);
